import CurrencyInput, { type IntlConfig } from 'react-currency-input-field';
import { SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import { intlConfig } from '@pocapan/shared/utils/CurrencyUtil';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { t } from 'i18next';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';

interface PhotoCardPostProps {
  title: string;
  subtitle: string;
  imageUrl: string;
  badges?: string[];
  showInput?: boolean;
  showClose?: boolean;
  inputValue?: string;
  onInputChange?: (value: string | undefined) => void;
  onClose?: () => void;
}

const PhotoCardPost = ({
  title,
  subtitle,
  imageUrl,
  badges = [],
  showInput = false,
  showClose = false,
  inputValue,
  onInputChange,
  onClose
}: PhotoCardPostProps) => {
  const user = useUserQuery();
  return (
    <div className="relative w-full bg-background-light dark:bg-background-elevated rounded-[12px] shadow-[inset_0_0_0_1px_var(--color-separator1)] pl-[16px] pr-[10px] py-[16px] flex flex-col">
      <div className="flex flex-row gap-[14px] items-center">
        <ThumbnailPhotoCard imageUrl={imageUrl} className="w-[48px] h-[75px]" />
        {showClose && (
          <button
            type="button"
            className="absolute top-[10px] right-[10px] p-[6px] flex items-center justify-center bg-secondary-light dark:bg-secondary-dark rounded-full"
            onClick={onClose}
          >
            <Xmark size={16} className="text-on-secondary" />
          </button>
        )}

        <div className="flex flex-col gap-[7px] leading-none">
          <div className="h-[18px] text-[15px] font-semibold text-text-main-lv1 max-w-[180px] truncate">
            {title}
          </div>
          <div className="h-[17px] text-[14px] font-medium text-text-main-lv3 max-w-[180px] truncate">
            {subtitle}
          </div>
          {badges.length > 0 && (
            <HorizontalScroll>
              <div className="flex flex-row gap-[4px] max-w-[220px]">
                {badges.map((badge) => (
                  <SquareBadge
                    key={badge}
                    size="medium"
                    colorVariant="gray"
                    styleVariant="solid"
                    className="bg-surface-lv2"
                  >
                    {badge}
                  </SquareBadge>
                ))}
              </div>
            </HorizontalScroll>
          )}
        </div>
      </div>

      {showInput && (
        <>
          <div className="w-full h-px bg-separator1 mt-[14px] mb-[12px]" />
          <div className="flex flex-row items-center w-full pr-[6px]">
            {!inputValue && (
              <span className="text-[17px] font-medium text-placeholder-light dark:text-placeholder-dark min-w-fit mr-[10px] leading-none">
                {t('NewTradingPhotoCards.inputPricePlaceholder')}
              </span>
            )}
            <CurrencyInput
              className="w-full focus:outline-none text-right text-text-main-lv2 text-[17px] font-medium caret-text-primary leading-none  bg-transparent"
              disabled={false}
              allowNegativeValue={false}
              value={inputValue}
              onValueChange={onInputChange}
              intlConfig={intlConfig(user.data) as IntlConfig}
            />
            {!inputValue && (
              <span className="text-[17px] font-medium text-placeholder-light dark:text-placeholder-dark ml-[2px] leading-none">
                {t('NewTradingPhotoCards.inputPriceUnit')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoCardPost;
